import React, { useState, useEffect } from 'react';
import { X, Warning, UserCircle, Coin, Handshake } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

export const BettingDisclaimer = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already accepted disclaimer
    const accepted = localStorage.getItem('sharpbetai_disclaimer_accepted');
    if (!accepted) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('sharpbetai_disclaimer_accepted', 'true');
    localStorage.setItem('sharpbetai_disclaimer_date', new Date().toISOString());
    setIsVisible(false);
  };

  const handleDecline = () => {
    // Redirect to external page since user doesn't accept terms
    window.location.href = 'https://www.google.com';
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />
      
      <div className="relative bg-accent border border-primary/30 rounded-sm max-w-lg w-full shadow-2xl shadow-primary/10" data-testid="betting-disclaimer-modal">
        {/* Header */}
        <div className="bg-primary/10 border-b border-primary/20 p-6">
          <div className="flex items-center gap-3">
            <Warning weight="fill" className="text-primary text-3xl" />
            <h2 className="text-2xl font-heading font-black text-white">
              {t('disclaimerTitle')}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Age Restriction */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-risk-high/20 rounded-full flex items-center justify-center flex-shrink-0">
              <UserCircle weight="duotone" className="text-risk-high text-2xl" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-1">{t('disclaimerAge')}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{t('disclaimerAgeText')}</p>
            </div>
          </div>

          {/* No Profit Guarantee */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-risk-medium/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Coin weight="duotone" className="text-risk-medium text-2xl" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-1">{t('disclaimerNoGuarantee')}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{t('disclaimerNoGuaranteeText')}</p>
            </div>
          </div>

          {/* User Responsibility */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Handshake weight="duotone" className="text-secondary text-2xl" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-1">{t('disclaimerResponsibility')}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{t('disclaimerResponsibilityText')}</p>
            </div>
          </div>

          {/* Responsible Gambling */}
          <div className="bg-white/5 border border-white/10 rounded-sm p-4">
            <h3 className="text-sm font-bold text-white mb-1">{t('disclaimerResponsibleGambling')}</h3>
            <p className="text-white/50 text-xs leading-relaxed">{t('disclaimerResponsibleGamblingText')}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-white/10 p-6 flex gap-4">
          <button
            onClick={handleDecline}
            className="flex-1 px-6 py-3 border border-white/20 text-white/70 font-semibold rounded-full hover:border-white/40 hover:text-white transition-all"
            data-testid="disclaimer-decline-btn"
          >
            {t('disclaimerDecline')}
          </button>
          <button
            onClick={handleAccept}
            className="flex-1 px-6 py-3 bg-primary text-background font-black rounded-full hover:bg-primary/90 transition-all shadow-lg"
            data-testid="disclaimer-accept-btn"
          >
            {t('disclaimerAccept')}
          </button>
        </div>
      </div>
    </div>
  );
};
